import classNames from 'classnames';
import { ReactNode } from 'react';

export type ToggleRowOption = {
  label: string | ReactNode;
  value: number;
  icon?: ReactNode;
  className?: string;
};

interface ToggleRowProps {
  options: ToggleRowOption[];
  selected?: number;
  onSelectionChange?: (value: number) => void;
  className?: string;
}

export default function ToggleRow({ options, selected, onSelectionChange, className }: ToggleRowProps) {
  const handleSelect = (value: number) => {
    onSelectionChange?.(value);
  };

  return (
    <div className={classNames(className, 'inline-flex self-start gap-1 rounded-lg bg-adm-surface2 p-1')}>
      {options.map((option, index) => (
        <button
          key={index}
          onClick={() => handleSelect(option.value)}
          data-selected={selected === option.value}
          className={classNames(
            'px-4 py-2 rounded-md font-medium text-sm transition-all duration-200 ease-in-out',
            'data-[selected=true]:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-adm-accent',
            // An option's own className owns its colours; the defaults below would
            // out-specify it (data-variants compile to `.class[data-selected]`).
            option.className
              ? [
                  // Swatch options (dark / light card style) read as samples of the thing
                  // they set, so the accent ring marks the choice rather than a fill swap.
                  'ring-1 ring-inset ring-black/10',
                  'data-[selected=true]:ring-2 data-[selected=true]:ring-adm-accent data-[selected=true]:ring-offset-1',
                  'data-[selected=false]:opacity-60 data-[selected=false]:hover:opacity-100',
                ]
              : [
                  'data-[selected=true]:scale-105',
                  'data-[selected=true]:bg-adm-surface data-[selected=true]:text-adm-ink',
                  'data-[selected=false]:text-adm-ink2 data-[selected=false]:hover:text-adm-ink data-[selected=false]:hover:bg-adm-surface3',
                ],
            option.className,
          )}
        >
          <span className="inline-flex items-center gap-2">
            {option.icon}
            {option.label}
          </span>
        </button>
      ))}
    </div>
  );
}
